import { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Code,
  Quote,
  Undo,
  Redo,
  Play,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Simple JS-only runner; other languages are rendered but not executed
function runSelectedCode(editor, language, setOutput) {
  if (!editor) return;

  let code = '';
  const { state } = editor;
  const { from, to } = state.selection;

  state.doc.nodesBetween(from, to, (node) => {
    if (node.type.name === 'codeBlock') {
      code = node.textContent;
      return false; // stop after first matching block in selection
    }
    return undefined;
  });

  if (!code.trim()) {
    setOutput('No code found. Click inside a code block before running.');
    return;
  }

  if (language !== 'javascript') {
    setOutput('Running code is currently supported only for JavaScript. The block is still rendered as formatted code.');
    return;
  }

  try {
    const logs = [];
    const originalLog = console.log;
    console.log = (...args) => {
      logs.push(args.join(' '));
      originalLog(...args);
    };

    let result;
    try {
      // Using Function constructor so authors can quickly run small snippets.
      // This runs only in the browser, not on the server.
      // eslint-disable-next-line no-new-func
      const fn = new Function(code);
      result = fn();
    } finally {
      console.log = originalLog;
    }

    if (result !== undefined) {
      logs.push(String(result));
    }

    setOutput(logs.join('\n') || 'Program finished with no output.');
  } catch (err) {
    setOutput(String(err));
  }
}

const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="border-b border-gray-200 p-2 flex flex-wrap gap-1 bg-gray-50">
      {/* Inline styles */}
      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        variant={editor.isActive('bold') ? 'default' : 'outline'}
        size="sm"
        data-testid="editor-bold-button"
      >
        <Bold size={16} />
      </Button>
      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        variant={editor.isActive('italic') ? 'default' : 'outline'}
        size="sm"
        data-testid="editor-italic-button"
      >
        <Italic size={16} />
      </Button>

      {/* Headings */}
      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'outline'}
        size="sm"
        data-testid="editor-h1-button"
      >
        <Heading1 size={16} />
      </Button>
      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'outline'}
        size="sm"
        data-testid="editor-h2-button"
      >
        <Heading2 size={16} />
      </Button>
      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        variant={editor.isActive('heading', { level: 3 }) ? 'default' : 'outline'}
        size="sm"
        data-testid="editor-h3-button"
      >
        <Heading3 size={16} />
      </Button>
      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
        variant={editor.isActive('heading', { level: 4 }) ? 'default' : 'outline'}
        size="sm"
        data-testid="editor-h4-button"
      >
        <Heading4 size={16} />
      </Button>

      {/* Lists */}
      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        variant={editor.isActive('bulletList') ? 'default' : 'outline'}
        size="sm"
        data-testid="editor-bullet-list-button"
      >
        <List size={16} />
      </Button>
      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        variant={editor.isActive('orderedList') ? 'default' : 'outline'}
        size="sm"
        data-testid="editor-ordered-list-button"
      >
        <ListOrdered size={16} />
      </Button>

      {/* Code block & quote */}
      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        variant={editor.isActive('codeBlock') ? 'default' : 'outline'}
        size="sm"
        data-testid="editor-code-button"
      >
        <Code size={16} />
      </Button>
      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        variant={editor.isActive('blockquote') ? 'default' : 'outline'}
        size="sm"
        data-testid="editor-quote-button"
      >
        <Quote size={16} />
      </Button>

      <div className="border-l border-gray-300 mx-2" />

      {/* Undo / Redo */}
      <Button
        type="button"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().chain().focus().undo().run()}
        variant="outline"
        size="sm"
        data-testid="editor-undo-button"
      >
        <Undo size={16} />
      </Button>
      <Button
        type="button"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().chain().focus().redo().run()}
        variant="outline"
        size="sm"
        data-testid="editor-redo-button"
      >
        <Redo size={16} />
      </Button>
    </div>
  );
};

export default function BlogEditor({ content, onChange }) {
  const [codeOutput, setCodeOutput] = useState('');
  const [currentLanguage, setCurrentLanguage] = useState('plaintext');

  const editor = useEditor({
    extensions: [
      // StarterKit already includes headings (H1–H6), bullet list, ordered list,
      // blockquote, and a basic codeBlock node.
      StarterKit,
    ],
    content: content || '<p>Start writing your blog post...</p>',
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  const isCodeActive = editor?.isActive('codeBlock');

  const handleLanguageChange = (event) => {
    const lang = event.target.value;
    setCurrentLanguage(lang);
    // We keep language purely for display / execution; the document itself
    // stays valid HTML and can still be used just to differentiate text.
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden" data-testid="blog-editor">
      <MenuBar editor={editor} />
      <EditorContent
        editor={editor}
        className="prose max-w-none p-4 min-h-[300px] focus:outline-none"
      />

      {isCodeActive && (
        <div className="border-t border-gray-200 bg-gray-50 px-3 py-2 text-xs sm:text-sm flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-gray-600">Code block tools:</span>
            <select
              value={currentLanguage}
              onChange={handleLanguageChange}
              className="border border-gray-300 bg-white px-2 py-1 text-xs sm:text-sm"
            >
              <option value="plaintext">No language (plain text)</option>
              <option value="javascript">JavaScript</option>
              <option value="python">Python</option>
              <option value="typescript">TypeScript</option>
            </select>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="flex items-center gap-1"
              onClick={() => runSelectedCode(editor, currentLanguage, setCodeOutput)}
              data-testid="editor-run-code-button"
            >
              <Play size={14} />
              Run
            </Button>
          </div>

          {codeOutput && (
            <div className="bg-black text-green-400 font-mono text-[11px] sm:text-xs px-3 py-2 max-h-40 overflow-y-auto rounded">
              {codeOutput.split('\n').map((line, idx) => (
                <div key={idx}>{line}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
